import {
  ExpressionSpecification,
  FilterSpecification,
  LayerProps,
} from 'shared/map-exports';
import { FONT_NAMES } from 'components/Map/styles/constants';
import {
  LabelTooltipBackground,
  WeatherStationArrowIcon,
  WeatherStationTooltipBackground,
  WindGustTooltipBackground,
} from 'components/Map/Icons';
import {
  METERS_PER_SEC_TO_MPH_FACTOR,
  TEMPERATURE_COLOR_STOPS,
} from 'features/weatherStations/weatherStations.constants';
import { SELECTED_FEATURE_HIGHLIGHT_COLOR } from 'components/Map/constants';
import {
  BASE_CIRCLE_RADIUS,
  MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
  MIN_ICON_ZOOM_LEVEL,
  MIN_LABEL_ZOOM_LEVEL,
  SOURCE_LAYER,
} from './WeatherStationsLayer.constants';

export const getPointIconStyle = (isInternalUser: boolean): LayerProps => {
  const style: LayerProps = {
    type: 'circle',
    minzoom: MIN_ICON_ZOOM_LEVEL,
    'source-layer': SOURCE_LAYER,
    paint: {
      'circle-color': [
        'case',
        ['!=', ['typeof', ['get', 'air_temp']], 'number'],
        TEMPERATURE_COLOR_STOPS[0].color, // fallback color for undefined/null/non-numeric values
        [
          'interpolate',
          ['linear'],
          ['+', ['*', ['get', 'air_temp'], 9 / 5], 32], // convert celsius to fahrenheit
          ...TEMPERATURE_COLOR_STOPS.flatMap((stop) => [stop.temp, stop.color]),
        ],
      ],
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_ICON_ZOOM_LEVEL,
        BASE_CIRCLE_RADIUS,
        MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
        6,
      ],
      'circle-stroke-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_ICON_ZOOM_LEVEL,
        1.25,
        MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
        2,
      ],
      'circle-stroke-color': '#000000',
    },
  };
  if (!isInternalUser) {
    style.filter = ['==', ['get', 'is_within_coverage_area'], true];
  }
  return style;
};

export const getArrowIconStyle = (isInternalUser: boolean): LayerProps => {
  const style: LayerProps = {
    type: 'symbol',
    minzoom: MIN_ICON_ZOOM_LEVEL,
    'source-layer': SOURCE_LAYER,
    layout: {
      'icon-image': WeatherStationArrowIcon.name,
      'icon-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_ICON_ZOOM_LEVEL,
        0.625,
        MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
        1.125,
      ],
      'icon-allow-overlap': true,
      'icon-ignore-placement': false,
      'icon-rotation-alignment': 'map',
      // Synoptic wind direction is the direction the wind is coming FROM, so reverse it
      'icon-rotate': [
        '%',
        ['+', ['coalesce', ['get', 'wind_direction'], 0], 180],
        360,
      ],
    },
    paint: {
      'icon-opacity': ['case', ['==', ['get', 'wind_direction'], null], 0, 1],
    },
  };

  if (!isInternalUser) {
    style.filter = ['==', ['get', 'is_within_coverage_area'], true];
  }

  return style;
};

const getMphNumberExpression = (propKey: string): ExpressionSpecification => [
  'case',
  ['==', ['get', propKey], 0], // Handles the case of exactly 0
  '0',
  // Check if the fractional part is zero after conversion
  ['==', ['%', ['*', ['get', propKey], METERS_PER_SEC_TO_MPH_FACTOR], 1], 0],
  [
    'to-string',
    ['round', ['*', ['get', propKey], METERS_PER_SEC_TO_MPH_FACTOR]],
  ], // Convert m/s to mph, round, then convert to string
  // Otherwise, format with one decimal place
  [
    'number-format',
    ['*', ['get', propKey], METERS_PER_SEC_TO_MPH_FACTOR],
    { 'min-fraction-digits': 0, 'max-fraction-digits': 1 },
  ],
];

const commonLayoutLabelStyles: LayerProps['layout'] = {
  'text-font': FONT_NAMES.regular,
  'text-size': 12,
  'text-offset': [0, 2.75],
  'text-max-width': 1000,
  'icon-text-fit': 'both',
  'icon-text-fit-padding': [5, 12, 5, 12], // [top, right, bottom, left]
};

export const getLabelStyle = (isInternalUser: boolean): LayerProps => {
  const filter: FilterSpecification = [
    'all',
    ['!=', ['get', 'wind_speed'], null],
    ['!=', ['get', 'wind_gust'], null],
  ];

  if (!isInternalUser) {
    filter.push(['==', ['get', 'is_within_coverage_area'], true]);
  }

  return {
    type: 'symbol',
    minzoom: MIN_LABEL_ZOOM_LEVEL,
    'source-layer': SOURCE_LAYER,
    layout: {
      ...commonLayoutLabelStyles,
      'text-field': [
        'format',
        getMphNumberExpression('wind_speed'),
        ['literal', ' mph'],
        { 'font-scale': 0.8 },
        // Leave enough space between values for icons/separator
        ['literal', '       '],
        ['literal', '       '],
        getMphNumberExpression('wind_gust'),
        ['literal', ' mph'],
        { 'font-scale': 0.8 },
      ],
      // Icon properties for the background box
      'icon-image': WeatherStationTooltipBackground.name,
    },
    filter,
  };
};

export const getWindSpeedLabelStyle = (isInternalUser: boolean): LayerProps => {
  const filter: FilterSpecification = [
    'all',
    ['!=', ['get', 'wind_speed'], null],
    ['==', ['get', 'wind_gust'], null],
  ];

  if (!isInternalUser) {
    filter.push(['==', ['get', 'is_within_coverage_area'], true]);
  }

  return {
    type: 'symbol',
    minzoom: MIN_LABEL_ZOOM_LEVEL,
    'source-layer': SOURCE_LAYER,
    layout: {
      ...commonLayoutLabelStyles,
      'text-field': [
        'format',
        getMphNumberExpression('wind_speed'),
        ['literal', ' mph'],
        { 'font-scale': 0.8 },
      ],
      // Icon properties for the background box
      'icon-image': LabelTooltipBackground.name,
    },
    filter,
  };
};

export const getWindGustLabelStyle = (isInternalUser: boolean): LayerProps => {
  const filter: FilterSpecification = [
    'all',
    ['==', ['get', 'wind_speed'], null],
    ['!=', ['get', 'wind_gust'], null],
  ];

  if (!isInternalUser) {
    filter.push(['==', ['get', 'is_within_coverage_area'], true]);
  }

  return {
    type: 'symbol',
    minzoom: MIN_LABEL_ZOOM_LEVEL,
    'source-layer': SOURCE_LAYER,
    layout: {
      ...commonLayoutLabelStyles,
      'text-field': [
        'format',
        getMphNumberExpression('wind_gust'),
        ['literal', ' mph'],
        { 'font-scale': 0.8 },
      ],
      // Icon properties for the background box
      'icon-image': WindGustTooltipBackground.name,
      'icon-text-fit-padding': [5, 12, 5, 32], // [top, right, bottom, left]
    },
    filter,
  };
};

export const getSelectedPointIconStyle = (
  isInternalUser: boolean,
  selectedStationId?: number,
): LayerProps => {
  const filter: FilterSpecification = isInternalUser
    ? ['==', ['id'], selectedStationId ?? null]
    : [
        'all',
        ['==', ['get', 'is_within_coverage_area'], true],
        ['==', ['id'], selectedStationId ?? null],
      ];

  return {
    type: 'circle',
    minzoom: MIN_ICON_ZOOM_LEVEL,
    'source-layer': SOURCE_LAYER,
    paint: {
      'circle-radius': 0,
      'circle-color': SELECTED_FEATURE_HIGHLIGHT_COLOR,
      'circle-blur': 1.25,
    },
    filter,
  };
};
